import { useId, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { numero } from "../lib/admin";
import { horaComArtigo } from "../lib/horaNoTexto";
import {
  vazioDoPeriodo,
  FAIXAS,
  ROTULO_DA_ORIGEM,
  buscaSoDoPeriodo,
  chaveDoPedido,
  chaveDoPeriodo,
  diaBr,
  AINDA_SEM_AVALIACAO,
  faixaDaMedia,
  lerVisaoGeral,
  metaLegivel,
  notaLegivel,
  numeroDaVariacao,
  pctLegivel,
  planilhaDasFerramentas,
  quandoLegivel,
  rotuloDoAnterior,
  sentidoDaVariacao,
  unidadeDaVariacao,
  useLeitura,
  variacaoLegivel,
  type AvaliacaoDaLista,
  type CampoDaNota,
  type FaixaDaMedia,
  type FerramentaEmAtencao,
  type Granularidade,
  type LinhaDaFerramenta,
  type MotivoDeAtencao,
  type Numeros,
  type PedidoDePeriodo,
  type Periodo,
  type UnidadeDaVariacao,
  type Variacao,
  type VisaoGeral,
} from "../lib/avaliacoes";
import { AvisoDaLeitura, LinhaDoPeriodo, SeloDaMedia, SinalDaVariacao, fraseDaImportacao } from "./AdminAvaliacoesUi";
import {
  BarrasHorizontais,
  GraficoDeColunas,
  GraficoDeLinhas,
  PontosPorLinha,
  type ItemDasBarras,
  type ItemDosPontos,
  type SerieDoGrafico,
} from "./AdminAvaliacoesGraficos";
import DialogoDaAvaliacao from "./AdminAvaliacoesDialogo";

/**
 * Admin › Avaliações › Visão geral (000328) — o Dashboard do painel antigo
 * (`feedbacks-cf`), que o time de Produtos lia: os números do período contra o
 * anterior, quem está em atenção, a evolução, as distribuições, as avaliações
 * baixas mais recentes e o diagnóstico das três notas de cada ferramenta.
 *
 * ---------------------------------------------------------------------------
 * ZERO conta aqui
 * ---------------------------------------------------------------------------
 * Tudo sai de UMA leitura (`av_admin_visao_geral`): as médias, as faixas
 * CONTADAS, o NPS adaptado, as variações (cada uma na sua unidade), os baldes do
 * eixo do tempo e quem entra em atenção, na ordem certa. No painel antigo cada
 * gráfico refazia a conta sobre as linhas cruas no navegador — e havia DUAS
 * contas com o nome NPS, uma no Dashboard e outra no Comparar. Aqui a tela
 * ordena, formata e desenha.
 *
 * O que ela decide sozinha é só PINTURA, e cada caso está nomeado onde mora:
 *   · a cor de uma média pela faixa (`FAIXAS`, em `lib/avaliacoes.ts`);
 *   · o tom de atenção do cartão das baixas acima de 15%
 *     (`BAIXAS_EM_ATENCAO_ACIMA_DE`, a regra do painel antigo).
 * A LARGURA de cada fatia da barra do NPS adaptado é geometria: ela cresce com
 * a contagem da categoria, e não com um percentual. O percentual ESCRITO ao lado
 * vem do banco desde 29/09 (`promotores_pct`, `neutros_pct`, `detratores_pct`);
 * antes a tela o refazia, e era a única conta de negócio dela — a segunda
 * definição de um percentual que o banco já sabia fazer.
 *
 * ---------------------------------------------------------------------------
 * O período é o da RESPOSTA
 * ---------------------------------------------------------------------------
 * A raiz declara de que período são os números que mostra
 * (`data-periodo-dos-dados`, o que o banco respondeu, e não o que a URL pede):
 * entre o clique e a resposta, a tela do período antigo continua de pé — mais
 * apagada, com `aria-busy` —, e quem espera "pronto" sem olhar o período lê os
 * números errados (a cicatriz da busca da Central, 14/09). Os LINKS, esses,
 * levam o período da URL, como os do despacho: é para ele que a pessoa vai.
 */

// ── As regras de pintura ────────────────────────────────────────────────────

/**
 * Acima disto, o cartão das baixas ganha o tom de ATENÇÃO — a regra do painel
 * antigo ("Taxa de detratores (≤ 5)", que ficava vermelha acima de 15%). Só
 * pinta: o percentual é o do banco, e ninguém entra ou sai de lista nenhuma
 * por causa deste número.
 */
const BAIXAS_EM_ATENCAO_ACIMA_DE = 15;

/**
 * Quantas ferramentas em atenção aparecem antes do "mostrar as outras". Cinco
 * era o corte do painel antigo, e lá ele era SILENCIOSO (`slice(0, 5)`): a
 * sexta ferramenta em atenção simplesmente não existia. Aqui o banco devolve
 * todas, e a tela diz quantas faltam.
 */
const VISIVEIS_EM_ATENCAO = 5;

// ── Os endereços ────────────────────────────────────────────────────────────

/**
 * O endereço de uma sub-aba com os parâmetros dela e SÓ o período — os filtros
 * de uma sub-aba não significam nada na outra (a regra do despacho). `periodo`
 * é o `buscaSoDoPeriodo` do pedido, já em texto.
 */
function endereco(caminho: string, periodo: string, extra: Record<string, string> = {}): string {
  const busca = new URLSearchParams(extra);
  new URLSearchParams(periodo).forEach((valor, chave) => busca.append(chave, valor));
  const texto = busca.toString();
  return texto ? `${caminho}?${texto}` : caminho;
}

// ── Os textos ───────────────────────────────────────────────────────────────

/** "1 avaliação", "974 avaliações": o número com a palavra no singular ou no plural. */
function contagem(n: number, singular: string, plural: string): string {
  return `${numero(n)} ${n === 1 ? singular : plural}`;
}

/**
 * O NPS adaptado como NÍVEL: sem o "+", que aqui é da VARIAÇÃO — com o sinal
 * nos dois, "79" e "+5" lado a lado no mesmo cartão se leriam como duas
 * variações. E o menos é o sinal de menos de verdade, que o leitor de tela lê.
 */
function npsLegivel(n: number | null): string {
  if (n === null) return "—";
  return n < 0 ? `−${Math.abs(n)}` : String(n);
}

/** O motivo de atenção como frase curta, no singular e no plural certos. */
function textoDoMotivo(m: MotivoDeAtencao): string {
  switch (m.tipo) {
    case "queda":
      // "1,20 ponto" e "2,40 pontos": o plural começa no 2, como em "1,5 milhão".
      return `caiu ${notaLegivel(m.valor)} ${Math.abs(m.valor) < 2 ? "ponto" : "pontos"}`;
    case "media_baixa":
      return `média baixa (${notaLegivel(m.valor)})`;
    case "baixas":
      return contagem(m.valor, "avaliação baixa", "avaliações baixas");
    case "abaixo_da_meta":
      return `abaixo da meta ${metaLegivel(m.valor)}`;
  }
}

/** A granularidade do eixo do tempo, dita como a frase a diz. */
const POR: Record<Granularidade, string> = {
  hora: "por hora",
  dia: "por dia",
  semana: "por semana, começando na segunda",
  mes: "por mês",
};

/** O mesmo, para o título da tabela que traz os números do gráfico. */
const EM_CADA: Record<Granularidade, string> = {
  hora: "em cada hora",
  dia: "em cada dia",
  semana: "em cada semana",
  mes: "em cada mês",
};

/**
 * O que a granularidade esconde, quando esconde algo: a primeira semana pode
 * começar numa quarta e a última pode acabar hoje (o banco recorta os baldes
 * pelo período), e quem lê uma coluna curta no fim acharia que caiu.
 */
const RESSALVA: Record<Granularidade, string> = {
  hora: " — as horas de São Paulo",
  dia: "",
  semana: "; a primeira e a última semana podem ter menos de sete dias, recortadas pelo período",
  mes: "; o primeiro e o último mês podem estar incompletos, recortados pelo período",
};

function intervaloDoPeriodo(p: Periodo): string {
  return p.de === p.ate ? `em ${diaBr(p.de)}` : `de ${diaBr(p.de)} a ${diaBr(p.ate)}`;
}

/**
 * Com o que os números são comparados já está dito UMA vez, logo acima, na
 * `LinhaDoPeriodo` ("comparado com 01/08/2026 a 30/08/2026"). Repetir as datas
 * no bloco, ou em cada um dos seis cartões, seria a mesma informação sete vezes
 * (a cicatriz do "3 de 12" em três lugares da mesma cabine) — e a data longa
 * quebraria em duas linhas no cartão estreito. Cada cartão diz só "contra o
 * anterior". O bloco só fala da comparação no caso que a linha do alto NÃO
 * conta: o período anterior existe e não teve avaliação nenhuma.
 */
function avisoDoAnterior(v: VisaoGeral): string | undefined {
  const anterior = rotuloDoAnterior(v.periodo);
  if (!anterior || !v.indicadores.anterior || v.indicadores.anterior.avaliacoes > 0) return undefined;
  // Nas mesmas horas (000334) o anterior foi CORTADO, e a frase diz até quando:
  // "ontem não teve avaliação" seria falso para um dia que teve 35, só que depois
  // das 08h30 (a revisão de 02/10).
  const corte = v.periodo.comparacao === "mesmas_horas" && v.periodo.anterior?.comparado_ate ? quandoLegivel(v.periodo.anterior.comparado_ate) : null;
  const ate = corte ? `, até ${v.periodo.dias > 1 ? `${corte.dia.slice(0, 5)}, ` : ""}${horaComArtigo(corte.hora)}` : "";
  return `O período anterior (${anterior}${ate}) não teve avaliação de aluno, então não há com o que comparar.`;
}

// ── As cores da variação e da faixa ─────────────────────────────────────────

type Sentido = ReturnType<typeof sentidoDaVariacao>;

/** A seta e o sinal DIZEM o sentido; a cor só confirma (e quem não distingue a cor ouve a palavra). */
const COR_DO_SENTIDO: Record<Sentido, string> = {
  melhor: "text-aviso-tinta",
  pior: "text-erro-tinta",
  igual: "text-cinza",
  sem: "text-cinza",
};
/** O que o leitor de tela ouve depois da variação. "Igual", e não "sem mudança", para as três palavras serem da mesma família. */
const PALAVRA_DO_SENTIDO: Record<Sentido, string> = {
  melhor: "melhor",
  pior: "pior",
  igual: "igual",
  sem: "",
};

/**
 * Os tons das faixas, os mesmos do `SeloDaMedia` (que não os exporta): a célula
 * do diagnóstico é o selo em forma de célula de tabela, e tons diferentes para
 * a mesma faixa diriam que são duas regras.
 */
const TOM_DA_FAIXA: Record<FaixaDaMedia, string> = {
  satisfeita: "bg-aviso-fundo text-aviso-tinta",
  intermediaria: "bg-chip text-chip-tinta",
  baixa: "bg-erro-fundo text-erro-tinta",
};

const NOTAS: { campo: CampoDaNota; rotulo: string }[] = [
  { campo: "utilidade", rotulo: "Utilidade" },
  { campo: "facilidade", rotulo: "Facilidade" },
  { campo: "satisfacao", rotulo: "Satisfação" },
];

// ── Os ícones (dimensão intrínseca: a cicatriz das 34 setas gigantes) ───────

function IconeBaixar() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      className="size-4 shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 4v11M7 10l5 5 5-5M5 20h14" />
    </svg>
  );
}

function IconeAtencao() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      className="size-4 shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 3.5 2.5 20h19z" />
      <path d="M12 10v4.5M12 17.2v.1" />
    </svg>
  );
}

// ── O bloco ─────────────────────────────────────────────────────────────────

type AtributosDeDado = { [chave: `data-${string}`]: string | number | boolean | undefined };

/**
 * Cada bloco da Visão geral: a superfície, o título `h2` e o grupo que o leitor
 * de tela anuncia pelo título. `acao` é o link à direita do título ("Ver todas
 * as baixas").
 */
function Bloco({
  titulo,
  nota,
  acao,
  atributos,
  children,
}: {
  titulo: string;
  nota?: ReactNode;
  acao?: ReactNode;
  atributos?: AtributosDeDado;
  children?: ReactNode;
}) {
  const id = useId();
  return (
    <section className="surface min-w-0 p-5 sm:p-6" role="group" aria-labelledby={id} {...atributos}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 id={id} className="font-display text-lg font-bold text-titulo">
          {titulo}
        </h2>
        {acao}
      </div>
      {nota && (
        <div className="mt-1 max-w-3xl text-xs leading-relaxed text-cinza" data-nota-do-bloco>
          {nota}
        </div>
      )}
      {children}
    </section>
  );
}

// ── A sub-aba ───────────────────────────────────────────────────────────────

export default function AdminAvaliacoesVisao({ pedido }: { pedido: PedidoDePeriodo }) {
  const { estado } = useLeitura((s) => lerVisaoGeral(pedido, s), chaveDoPedido(pedido));
  // Os estados moram ANTES do retorno antecipado: hook depois de `return`
  // condicional mata a tela inteira no segundo render (a cicatriz do Radar,
  // "Minified React error #310").
  const [aberta, setAberta] = useState<AvaliacaoDaLista | null>(null);
  const [exportada, setExportada] = useState<{ periodo: string; ferramentas: number } | null>(null);
  const idDaFraseDaPlanilha = useId();

  if (estado.fase !== "pronto") return <AvisoDaLeitura estado={estado} />;
  const { dados, atualizando } = estado;
  const periodoNaBusca = buscaSoDoPeriodo(pedido);
  const chave = chaveDoPeriodo(dados.periodo);
  const linkDaPagina = (slug: string) => endereco("/admin/avaliacoes/ferramentas", periodoNaBusca, { card: slug });
  const semFerramenta = dados.ferramentas.length === 0;
  const importacao = fraseDaImportacao(dados.importacao);

  function exportar() {
    planilhaDasFerramentas(dados);
    // O arquivo vai para a pasta de downloads sem a página mudar nada: sem esta
    // frase, o clique parece não ter feito coisa nenhuma.
    setExportada({ periodo: chave, ferramentas: dados.ferramentas.length });
  }

  return (
    <div
      className={`transition-opacity ${atualizando ? "opacity-60" : ""}`}
      aria-busy={atualizando}
      data-aba-das-avaliacoes="visao"
      data-painel-pronto={!atualizando}
      data-periodo-dos-dados={chave}
    >
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        {/* A coluna da esquerda tem BASE de 22rem, e não `flex-1`: com base zero
            o `flex-wrap` nunca a quebraria, e no celular ela espremeria ao lado
            do botão; e sem base nenhuma, a frase longa da importação tomaria a
            linha inteira e empurraria o «Exportar CSV» para baixo, dobrando a
            altura do cabeçalho. */}
        <div className="min-w-0 flex-[1_1_22rem]">
          <LinhaDoPeriodo periodo={dados.periodo} atualizando={atualizando} />
          {/* Até quando o hub sabe o que o app antigo recebeu, logo abaixo do
              período: as avaliações de lá chegam por importação, e sem a frase
              o dia de hoje e os últimos dias se leriam como queda. */}
          {importacao && (
            <p className="mt-1 text-xs leading-snug text-cinza" data-importacao-da-visao>
              {importacao}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <p className="max-w-xs text-xs leading-snug text-cinza" id={idDaFraseDaPlanilha}>
            {semFerramenta
              ? vazioDoPeriodo(
                  dados.periodo,
                  "Sem avaliação de aluno no período, não há relatório por ferramenta para exportar.",
                  "O hub ainda não tem avaliação deste período, e não há relatório por ferramenta para exportar.",
                )
              : dados.periodo.comparacao === "mesmas_horas"
                ? "O relatório por ferramenta deste período, comparado com o período anterior até a mesma hora."
                : dados.periodo.anterior
                ? "O relatório por ferramenta deste período, comparado com o período anterior."
                : "O relatório por ferramenta de todas as avaliações (em «Todos» não há comparação)."}
          </p>
          <button
            type="button"
            onClick={exportar}
            disabled={semFerramenta}
            aria-describedby={idDaFraseDaPlanilha}
            className="btn-contorno min-h-10 text-sm disabled:cursor-not-allowed disabled:border-dashed disabled:text-cinza"
            data-exportar-csv
          >
            <IconeBaixar />
            Exportar CSV
          </button>
        </div>
      </div>
      {exportada && exportada.periodo === chave && (
        <p className="mt-2 text-right text-xs text-cinza" role="status" data-planilha-gerada={exportada.ferramentas}>
          Planilha gerada com {contagem(exportada.ferramentas, "ferramenta", "ferramentas")} — ela vai para a pasta de
          downloads do navegador.
        </p>
      )}

      <div className="mt-5 space-y-5">
        <Indicadores dados={dados} />
        <Atencao dados={dados} periodoNaBusca={periodoNaBusca} linkDaPagina={linkDaPagina} />
        <Evolucao dados={dados} />
        {/* Lado a lado a partir de 72rem de janela, e não do `xl`: a conta dos
            seis números (ver `Indicadores`) — numa janela real de 1280px a área
            útil é de 1262px, e o `xl` não ligava. */}
        <div className="grid items-start gap-5 min-[72rem]:grid-cols-2">
          <DistribuicaoDoNps numeros={dados.indicadores.atual} aindaSemDado={dados.periodo.comparacao === "sem_dado_ainda"} />
          <PorFerramenta dados={dados} linkDaPagina={linkDaPagina} />
        </div>
        <MediaPorFerramenta dados={dados} linkDaPagina={linkDaPagina} />
        <BaixasRecentes dados={dados} periodoNaBusca={periodoNaBusca} linkDaPagina={linkDaPagina} aoAbrir={setAberta} />
        <Diagnostico dados={dados} linkDaPagina={linkDaPagina} />
        <ComoSeLe />
      </div>

      <DialogoDaAvaliacao avaliacao={aberta} aoFechar={() => setAberta(null)} linkDaFerramenta={linkDaPagina} />
    </div>
  );
}

// ── 1. Os números do período ────────────────────────────────────────────────

type Indicador = {
  /** O nome do campo em `Numeros`: é por ele que a prova compara a tela com a resposta do banco. */
  id: keyof Numeros;
  rotulo: string;
  valor: string;
  bruto: number | null;
  sufixo?: string;
  /** A contagem por trás do percentual: no `title` do número e, para o leitor de tela, junto da nota. */
  quantas?: number;
  nota?: string;
  /** O campo de `Variacao` de onde a variação veio — declarado no cartão, para a prova comparar com a resposta. */
  campoDaVariacao: keyof Variacao;
  variacao: number | null;
  unidade: UnidadeDaVariacao;
  maiorEMelhor: boolean;
  atencao?: boolean;
};

/** Em que situação a comparação está — decide o que a linha da variação diz. */
type Contexto = "sem_anterior" | "sem_dado_ainda" | "vazio" | "anterior_vazio" | "normal";

function Indicadores({ dados }: { dados: VisaoGeral }) {
  const a = dados.indicadores.atual;
  const v = dados.indicadores.variacao;
  const comAvaliacao = a.avaliacoes > 0;
  // O hub ainda sem avaliação do período (000334) não é «Todos»: a linha do
  // período já diz por quê, e o cartão não pode dizer "sem período anterior".
  const contexto: Contexto = dados.periodo.comparacao === "sem_dado_ainda"
    ? "sem_dado_ainda"
    : !dados.periodo.anterior
    ? "sem_anterior"
    : !comAvaliacao
      ? "vazio"
      : dados.indicadores.anterior?.avaliacoes === 0
        ? "anterior_vazio"
        : "normal";

  // As notas cabem numa linha do cartão mais estreito — 140px de texto a
  // 1280px, seis por linha: o `max-w-6xl` do painel dá 1104px de conteúdo, a
  // seção tira 48, as cinco folgas 60, e o cartão, borda e `p-3`. Cada linha a
  // mais em UM cartão alonga os seis, e a primeira dobra tem de mostrar também a
  // atenção, logo abaixo. As contagens das satisfeitas e das baixas vão no
  // `title` do número e para o leitor de tela — o percentual é o que se
  // compara, e a quantidade das baixas está também no link do bloco delas.
  //
  // A variação sai do campo NOMEADO, num lugar só: o nome e o valor nunca
  // divergem, e o cartão declara de que campo do banco o número veio (em dois
  // cartões ele não é o nome do indicador — `avaliacoes` varia em
  // `avaliacoes_pct`).
  const daVariacao = (campo: keyof Variacao) => ({ campoDaVariacao: campo, variacao: v[campo] });
  const indicadores: Indicador[] = [
    {
      id: "avaliacoes",
      rotulo: "Avaliações no período",
      valor: numero(a.avaliacoes),
      bruto: a.avaliacoes,
      nota: "de alunos",
      ...daVariacao("avaliacoes_pct"),
      unidade: "pct",
      maiorEMelhor: true,
    },
    {
      id: "media_geral",
      rotulo: "Média geral",
      valor: notaLegivel(a.media_geral),
      bruto: a.media_geral,
      sufixo: a.media_geral === null ? undefined : "/10",
      nota: "das três notas",
      ...daVariacao("media_geral"),
      unidade: "pontos",
      maiorEMelhor: true,
    },
    {
      id: "satisfeitas_pct",
      rotulo: "Satisfeitas",
      valor: pctLegivel(a.satisfeitas_pct),
      bruto: a.satisfeitas_pct,
      quantas: comAvaliacao ? a.satisfeitas : undefined,
      nota: `média ${FAIXAS.satisfeitaDesde} ou mais`,
      ...daVariacao("satisfeitas_pct"),
      unidade: "pp",
      maiorEMelhor: true,
    },
    {
      id: "nps_adaptado",
      rotulo: "NPS adaptado",
      valor: npsLegivel(a.nps_adaptado),
      bruto: a.nps_adaptado,
      // A fórmula está no bloco do NPS, logo abaixo; aqui vai o que evita o
      // erro de leitura mais provável, de quem conhece o NPS das pesquisas.
      nota: "não é o das pesquisas",
      ...daVariacao("nps_adaptado"),
      unidade: "inteiro",
      maiorEMelhor: true,
    },
    {
      id: "baixas_pct",
      rotulo: "Baixas",
      valor: pctLegivel(a.baixas_pct),
      bruto: a.baixas_pct,
      quantas: comAvaliacao ? a.baixas : undefined,
      nota: `média ${FAIXAS.baixaAte} ou menos`,
      ...daVariacao("baixas_pct"),
      unidade: "pp",
      // Menos é melhor: mais baixas é pior, e a cor da variação acompanha.
      maiorEMelhor: false,
      atencao: a.baixas_pct !== null && a.baixas_pct > BAIXAS_EM_ATENCAO_ACIMA_DE,
    },
    {
      id: "ferramentas_avaliadas",
      rotulo: "Ferramentas avaliadas",
      valor: numero(a.ferramentas_avaliadas),
      bruto: a.ferramentas_avaliadas,
      nota: "com avaliação",
      ...daVariacao("ferramentas_avaliadas_pct"),
      unidade: "pct",
      maiorEMelhor: true,
    },
  ];

  return (
    <Bloco titulo="Os números do período" nota={avisoDoAnterior(dados)} atributos={{ "data-indicadores": true }}>
      {!comAvaliacao && (
        <p className="mt-3 text-sm text-tinta" data-periodo-sem-avaliacao>
          {dados.periodo.comparacao === "sem_dado_ainda" ? "O hub ainda não tem avaliação deste período" : "Nenhuma avaliação de aluno no período"} — os
          números que dependem de avaliação ficam em branco (—).
        </p>
      )}
      {/* Seis por linha a partir de 72rem (1152px) de janela, e não a partir do
          `xl` (1280px): é em 1152 que o `max-w-6xl` do painel chega aos 1104px
          de conteúdo da conta acima. Medido em 29/09 pela prova de tela: uma
          janela de 1280×900 tem 1262×804 de área útil (a barra de rolagem e a
          moldura comem o resto), o `xl` não ligava, os números quebravam em
          duas linhas de três e a primeira ferramenta em atenção ia para 1008px. */}
      <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 min-[72rem]:grid-cols-6">
        {indicadores.map((i) => (
          <CartaoDoIndicador key={i.id} i={i} contexto={contexto} />
        ))}
      </dl>
      {a.equipe_fora > 0 && (
        <p className="mt-3 text-xs text-cinza" data-equipe-fora={a.equipe_fora}>
          {a.equipe_fora === 1
            ? "1 avaliação da equipe ficou fora das contas."
            : `${numero(a.equipe_fora)} avaliações da equipe ficaram fora das contas.`}
        </p>
      )}
    </Bloco>
  );
}

function CartaoDoIndicador({ i, contexto }: { i: Indicador; contexto: Contexto }) {
  return (
    <div
      className={`min-w-0 rounded-xl border p-3 ${i.atencao ? "border-alerta-tinta/40 bg-alerta-fundo" : "border-borda"}`}
      data-indicador={i.id}
      data-campo-da-variacao={i.campoDaVariacao}
      data-em-atencao={i.atencao ? "true" : undefined}
    >
      {/* Rótulo em caixa normal, e não em versal espaçada como os números da
          lista das Ferramentas: em versal, "AVALIAÇÕES NO PERÍODO" quebra em duas
          linhas no cartão de 140px, e os seis cartões crescem juntos. */}
      <dt className={`flex items-start gap-1.5 text-xs font-semibold ${i.atencao ? "text-alerta-tinta" : "text-cinza"}`}>
        {i.atencao && <IconeAtencao />}
        {i.rotulo}
      </dt>
      <dd className="mt-1.5 flex items-baseline gap-1">
        {/* Sem `tabular-nums`: ele dá a cada algarismo a largura do zero, e um
            número grande sozinho ("121") fica frouxo. Algarismo de largura fixa é
            para coluna que alinha na vertical — a tabela do diagnóstico. */}
        <span
          className={`font-display text-3xl leading-none font-bold ${i.atencao ? "text-alerta-tinta" : "text-titulo"}`}
          title={i.quantas !== undefined && i.nota ? `${contagem(i.quantas, "avaliação", "avaliações")} de ${i.nota}` : undefined}
          data-valor={i.bruto ?? ""}
        >
          {i.valor}
        </span>
        {i.sufixo && <span className="text-sm text-cinza">{i.sufixo}</span>}
      </dd>
      {i.nota && (
        <dd className="mt-1 text-xs leading-snug text-cinza">
          {i.nota}
          {i.quantas !== undefined && <span className="sr-only"> — {contagem(i.quantas, "avaliação", "avaliações")}</span>}
        </dd>
      )}
      {i.atencao && (
        // A palavra, além do tom: quem não distingue a cor fica sabendo por que
        // este cartão é diferente dos outros cinco.
        <dd className="text-xs font-semibold text-alerta-tinta" data-motivo-do-tom>
          acima de {BAIXAS_EM_ATENCAO_ACIMA_DE}%
        </dd>
      )}
      <dd className="mt-2 text-xs leading-snug">
        <VariacaoDoIndicador i={i} contexto={contexto} />
      </dd>
    </div>
  );
}

function VariacaoDoIndicador({ i, contexto }: { i: Indicador; contexto: Contexto }) {
  // Sem com o que comparar, a linha diz POR QUÊ — um "—" solto se leria como
  // defeito da tela. E com o período vazio não há variação nenhuma: "−100%"
  // contra ontem às oito da manhã seria alarme sobre um dia que mal começou.
  const semVariacao =
    contexto === "sem_dado_ainda"
      ? "ainda sem avaliação"
      : contexto === "sem_anterior"
      ? "sem período anterior"
      : contexto === "vazio"
        ? "sem avaliação no período"
        : i.variacao === null
          ? contexto === "anterior_vazio"
            ? "o anterior não teve avaliação"
            : "sem variação para mostrar"
          : null;
  if (semVariacao !== null || i.variacao === null) {
    return (
      <span className="text-cinza" data-variacao="" data-sentido="sem">
        {semVariacao}
      </span>
    );
  }
  const sentido = sentidoDaVariacao(i.variacao, i.maiorEMelhor);
  const palavras = unidadeDaVariacao(i.variacao, i.unidade);
  return (
    <>
      {/* Texto corrido, e não `inline-flex`: a unidade por extenso ("ponto
          percentual", desde 29/09) mal cabe junto do número no cartão de 140px,
          e no plural não cabe — e em `flex` o espaço entre os dois some e nada
          quebra. Aqui a unidade é UM pedaço que não se parte (`nowrap`), em peso
          normal para ocupar menos: se não couber, ela desce inteira para a linha
          de baixo, e o número fica com o sinal. O texto da variação continua
          sendo o de `variacaoLegivel` — é ele que o leitor de tela lê e que a
          prova compara com o banco. */}
      <span className={`font-semibold tabular-nums ${COR_DO_SENTIDO[sentido]}`} data-variacao={i.variacao} data-sentido={sentido}>
        <SinalDaVariacao v={i.variacao} className="mr-0.5 inline-block" />
        {i.unidade === "pct" ? variacaoLegivel(i.variacao, i.unidade) : numeroDaVariacao(i.variacao, i.unidade)}
        {palavras && (
          <>
            {" "}
            <span className="font-normal whitespace-nowrap">{palavras}</span>
          </>
        )}
        {PALAVRA_DO_SENTIDO[sentido] && <span className="sr-only"> ({PALAVRA_DO_SENTIDO[sentido]})</span>}
      </span>
      {/* Numa linha própria, e não emendada no número: no cartão estreito a
          frase quebraria no meio, e cada cartão com a quebra num lugar. */}
      <span className="block text-cinza">contra o anterior</span>
    </>
  );
}

// ── 2. Ferramentas em atenção ───────────────────────────────────────────────

function Atencao({
  dados,
  periodoNaBusca,
  linkDaPagina,
}: {
  dados: VisaoGeral;
  periodoNaBusca: string;
  linkDaPagina: (slug: string) => string;
}) {
  const [todas, setTodas] = useState(false);
  const idDaLista = useId();
  const lista = dados.atencao;
  // A ordem é a do banco (a pior média primeiro, depois a maior queda, depois o
  // nome): reordenar aqui seria a segunda definição de "quem vem primeiro".
  const visiveis = todas ? lista : lista.slice(0, VISIVEIS_EM_ATENCAO);
  const escondidas = lista.length - VISIVEIS_EM_ATENCAO;
  const comAvaliacao = dados.indicadores.atual.avaliacoes > 0;

  return (
    <Bloco
      titulo="Ferramentas em atenção"
      atributos={{ "data-atencao": true, "data-total-em-atencao": lista.length, "data-mostrando": visiveis.length }}
      acao={
        lista.length > 0 && (
          <p className="text-xs font-semibold text-alerta-tinta">{contagem(lista.length, "ferramenta", "ferramentas")}</p>
        )
      }
      nota={
        // UMA linha a 1280px: este bloco tem de aparecer na primeira dobra,
        // logo abaixo dos números. As regras são as do banco, escritas aqui só
        // como texto.
        lista.length > 0 && (
          <>
            Queda de 1 ponto ou mais, média abaixo de 6, 3 ou mais avaliações baixas, ou abaixo da meta — a pior média
            primeiro.
            {dados.periodo.comparacao === "sem_anterior" && " Em «Todos» não há período anterior, e a regra da queda não se aplica."}
          </>
        )
      }
    >
      {lista.length === 0 ? (
        <p className="mt-3 text-sm leading-relaxed text-cinza" data-atencao-vazia>
          {comAvaliacao
            ? "Tudo sob controle no período — nenhuma ferramenta cruzou os limites de atenção (queda de 1 ponto ou mais, média abaixo de 6, 3 ou mais avaliações baixas, ou média abaixo da meta)."
            : // "Tudo sob controle" sobre um período sem dado nenhum seria afirmar o
              // que ninguém mediu.
              vazioDoPeriodo(
                dados.periodo,
                "Sem avaliação de aluno no período, não há o que pôr em atenção.",
                "O hub ainda não tem avaliação deste período, e não há o que pôr em atenção.",
              )}
        </p>
      ) : (
        <>
          <ul className="mt-3 space-y-2" id={idDaLista}>
            {visiveis.map((t) => (
              <LinhaDeAtencao
                key={t.slug}
                t={t}
                periodo={dados.periodo}
                link={linkDaPagina(t.slug)}
                linkDasBaixas={endereco("/admin/avaliacoes/feedbacks", periodoNaBusca, { card: t.slug, media: "baixas" })}
              />
            ))}
          </ul>
          {escondidas > 0 && (
            <button
              type="button"
              onClick={() => setTodas((v) => !v)}
              aria-expanded={todas}
              aria-controls={idDaLista}
              className="mt-3 text-sm font-semibold text-link underline-offset-2 hover:underline"
              data-mostrar-outras-em-atencao
            >
              {todas
                ? `Mostrar só as ${VISIVEIS_EM_ATENCAO} primeiras`
                : escondidas === 1
                  ? "Mostrar a outra ferramenta em atenção"
                  : `Mostrar as outras ${numero(escondidas)} ferramentas em atenção`}
            </button>
          )}
        </>
      )}
    </Bloco>
  );
}

function LinhaDeAtencao({
  t,
  periodo,
  link,
  linkDasBaixas,
}: {
  t: FerramentaEmAtencao;
  periodo: Periodo;
  link: string;
  linkDasBaixas: string;
}) {
  const sentido = sentidoDaVariacao(t.variacao_media, true);
  // "Nova no período" seria falso para quem tem avaliação de meses atrás e só
  // não teve no período anterior: a frase diz o que o dado sabe. Em «Todos» a
  // linha não fala de variação nenhuma — a nota do bloco já diz, uma vez, que
  // não há período anterior, e cinco linhas repetindo isso seriam ruído.
  const variacao = !periodo.anterior
    ? null
    : t.variacao_media === null
      ? "sem avaliação no período anterior"
      : `${variacaoLegivel(t.variacao_media, "pontos")} contra o anterior`;
  return (
    // A linha inteira é o link para a página da ferramenta — pelo `::after` do
    // link do nome, esticado sobre ela —, e não um `<a>` em volta de tudo: dentro
    // dela há um SEGUNDO link (o das baixas), e link dentro de link não existe.
    <li
      className="relative rounded-xl border border-borda p-3 transition hover:border-alerta-tinta/40"
      data-atencao-ferramenta={t.slug}
      data-media={t.media_geral}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <SeloDaMedia media={t.media_geral} casas={2} />
        <Link
          to={link}
          className="font-semibold text-link underline-offset-2 after:absolute after:inset-0 after:rounded-xl hover:underline"
          data-abrir-ferramenta={t.slug}
        >
          {t.nome}
        </Link>
        {variacao !== null && (
          <span
            className={`text-xs font-semibold tabular-nums ${COR_DO_SENTIDO[sentido]}`}
            data-variacao-da-atencao={t.variacao_media ?? ""}
            data-sentido={sentido}
          >
            {variacao}
          </span>
        )}
        <ul className="flex flex-wrap gap-1.5" aria-label={`Por que ${t.nome} está em atenção`}>
          {t.motivos.map((m) => (
            <li
              key={m.tipo}
              className="rounded-full border border-alerta-tinta/40 bg-alerta-fundo px-2 py-0.5 text-xs font-semibold text-alerta-tinta tabular-nums"
              data-motivo={m.tipo}
              data-valor={m.valor}
            >
              {textoDoMotivo(m)}
            </li>
          ))}
        </ul>
        <span className="flex flex-wrap items-center gap-x-3 text-xs text-cinza sm:ml-auto">
          <span data-avaliacoes-da-atencao={t.avaliacoes}>{contagem(t.avaliacoes, "avaliação", "avaliações")}</span>
          {t.baixas > 0 && (
            <Link
              to={linkDasBaixas}
              className="relative z-10 font-semibold text-link underline-offset-2 hover:underline"
              data-ver-baixas-da-ferramenta={t.slug}
            >
              {t.baixas === 1 ? "Ver a avaliação baixa" : `Ver as ${numero(t.baixas)} avaliações baixas`}
            </Link>
          )}
        </span>
      </div>
    </li>
  );
}

// ── 3. O volume e a evolução da média ───────────────────────────────────────

function Evolucao({ dados }: { dados: VisaoGeral }) {
  const { periodo, serie } = dados;
  const g = periodo.granularidade;
  const atual = dados.indicadores.atual;
  const intervalo = intervaloDoPeriodo(periodo);
  // Estáveis entre renders que não trocam os dados (abrir o diálogo, por
  // exemplo): o gráfico não precisa refazer o desenho por causa disso.
  const volume = useMemo(() => serie.map((p) => p.avaliacoes), [serie]);
  const series = useMemo<SerieDoGrafico[]>(
    () => [
      {
        id: "media_geral",
        rotulo: "Média geral",
        p: 1,
        valores: serie.map((p) => p.media_geral),
        contagens: serie.map((p) => p.avaliacoes),
      },
    ],
    [serie],
  );

  return (
    // Lado a lado a partir de 72rem, como os seis números e pela mesma conta
    // (ver `Indicadores`): o `xl` não liga na janela real de 1280px.
    <div className="grid items-start gap-5 min-[72rem]:grid-cols-2">
      {/* O que cada gráfico já diz sozinho — o texto de "nenhuma avaliação" e a
          nota do ponto vazado — não se repete na nota do bloco: a mesma frase
          duas vezes, uma em cima da outra, é a cicatriz do "3 de 12". */}
      <Bloco
        titulo="Volume de avaliações"
        atributos={{ "data-volume-de-avaliacoes": true }}
        nota={`As avaliações de alunos ${POR[g]}${RESSALVA[g]}.`}
      >
        <div className="mt-4">
          <GraficoDeColunas
            baldes={serie}
            granularidade={g}
            valores={volume}
            descricao={`Colunas com as avaliações de alunos ${POR[g]}, ${intervalo}: ${contagem(atual.avaliacoes, "avaliação", "avaliações")} no período.`}
            rotuloDoValor={(n) => contagem(n, "avaliação", "avaliações")}
            rotuloDaTabela={`Ver as avaliações ${EM_CADA[g]}`}
            dado="volume"
            horizonte={periodo.horizonte}
            semValores={periodo.comparacao === "sem_dado_ainda" ? AINDA_SEM_AVALIACAO : undefined}
          />
        </div>
      </Bloco>
      <Bloco
        titulo="Evolução da média geral"
        atributos={{ "data-evolucao-da-media": true }}
        // A nota tem o MESMO número de linhas da do volume, ao lado: os dois
        // gráficos têm a mesma altura para os eixos se alinharem, e uma linha a
        // mais em cima de um deles desalinharia tudo. Que o eixo das notas não
        // começa no zero, os números do próprio eixo dizem.
        nota={`A média geral ${POR[g]}${RESSALVA[g]}.`}
      >
        <div className="mt-4">
          <GraficoDeLinhas
            baldes={serie}
            granularidade={g}
            series={series}
            descricao={`Linha da média geral das avaliações de alunos ${POR[g]}, ${intervalo}${
              atual.media_geral !== null ? `; a média do período é ${notaLegivel(atual.media_geral)}` : ""
            }.`}
            semPontos={periodo.comparacao === "sem_dado_ainda" ? AINDA_SEM_AVALIACAO : "Nenhuma avaliação de aluno no período."}
            rotuloDaTabela={`Ver a média geral ${EM_CADA[g]}`}
            dado="media"
            horizonte={periodo.horizonte}
            // Série ÚNICA: a marca de série única da casa, a mesma das colunas
            // do volume ao lado — e não a cor da série 1, que é a dos
            // promotores do NPS dois blocos abaixo.
            corDaSerieUnica="var(--cf-acento)"
          />
        </div>
      </Bloco>
    </div>
  );
}

// ── 4. O NPS adaptado e as avaliações por ferramenta ────────────────────────

const CATEGORIAS_DO_NPS = [
  {
    id: "promotores",
    campoDoPct: "promotores_pct",
    rotulo: "Promotores",
    regra: `média ${FAIXAS.promotorDesde} ou mais`,
    cor: "var(--cf-nps-promotor)",
  },
  {
    id: "neutros",
    campoDoPct: "neutros_pct",
    rotulo: "Neutros",
    regra: `média de ${FAIXAS.detratorAbaixoDe} a menos de ${FAIXAS.promotorDesde}`,
    cor: "var(--cf-nps-neutro)",
  },
  {
    id: "detratores",
    campoDoPct: "detratores_pct",
    rotulo: "Detratores",
    regra: `média abaixo de ${FAIXAS.detratorAbaixoDe}`,
    cor: "var(--cf-nps-detrator)",
  },
] as const satisfies ReadonlyArray<{ id: keyof Numeros; campoDoPct: keyof Numeros; rotulo: string; regra: string; cor: string }>;

function DistribuicaoDoNps({ numeros, aindaSemDado = false }: { numeros: Numeros; aindaSemDado?: boolean }) {
  const n = numeros.avaliacoes;
  const categorias = CATEGORIAS_DO_NPS.map((c) => ({
    ...c,
    quantas: numeros[c.id],
    // O percentual do BANCO. O `?? null` é a janela em que o banco ainda não o
    // devolve (a leitura da 000328): a tela escreve "—", e não refaz a conta.
    pct: numeros[c.campoDoPct] ?? null,
  }));
  return (
    <Bloco
      titulo="NPS adaptado"
      atributos={{ "data-distribuicao-nps": true, "data-nps-adaptado": numeros.nps_adaptado ?? "" }}
      nota={
        n > 0 ? (
          <>
            Promotores menos detratores dá o NPS adaptado do período:{" "}
            <strong className="font-semibold text-tinta tabular-nums">{npsLegivel(numeros.nps_adaptado)}</strong>.
          </>
        ) : undefined
      }
    >
      {n === 0 ? (
        <p className="mt-3 text-sm text-cinza" data-nps-vazio>
          {aindaSemDado ? "O hub ainda não tem avaliação deste período" : "Nenhuma avaliação de aluno no período"} — não há
          distribuição para mostrar.
        </p>
      ) : (
        <>
          {/* A barra é a proporção, e os números estão escritos logo abaixo —
              por isso ela é `aria-hidden`. Categoria com zero não entra: uma
              fatia de largura zero ainda desenharia a folga de 2px. Só as duas
              pontas da barra são arredondadas (4px, a ponta de marca do guia de
              gráficos), e as divisas entre as fatias são a folga, não um traço. */}
          <div className="mt-4 flex h-4 w-full gap-0.5" aria-hidden="true" data-barra-do-nps>
            {categorias
              .filter((c) => c.quantas > 0)
              .map((c) => (
                <span
                  key={c.id}
                  className="h-full first:rounded-l last:rounded-r"
                  // Com fatia de 0,1% a largura seria menos de um pixel: 3px de
                  // mínimo para a categoria que existe continuar existindo na tela.
                  style={{ flexGrow: c.quantas, flexBasis: 0, minWidth: 3, backgroundColor: c.cor }}
                  title={`${c.rotulo}: ${numero(c.quantas)} (${pctLegivel(c.pct)})`}
                  data-fatia={c.id}
                />
              ))}
          </div>
          <dl className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {categorias.map((c) => (
              <div
                key={c.id}
                className="rounded-xl border border-borda p-3"
                data-nps-categoria={c.id}
                data-quantas={c.quantas}
                data-pct={c.pct ?? ""}
              >
                <dt className="flex items-center gap-2 text-xs font-semibold text-cinza">
                  <span className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: c.cor }} aria-hidden="true" />
                  {c.rotulo}
                </dt>
                <dd className="mt-1 font-display text-xl font-bold text-titulo">{numero(c.quantas)}</dd>
                <dd className="text-xs tabular-nums text-cinza">
                  {pctLegivel(c.pct)} · {c.regra}
                </dd>
              </div>
            ))}
          </dl>
        </>
      )}
      <p className="mt-3 text-xs leading-relaxed text-cinza">
        O NPS adaptado é o percentual de promotores (média {FAIXAS.promotorDesde} ou mais) menos o de detratores (média
        abaixo de {FAIXAS.detratorAbaixoDe}). Não é o NPS das pesquisas, que pergunta de 0 a 10 se o aluno recomendaria o
        curso.
      </p>
    </Bloco>
  );
}

function PorFerramenta({ dados, linkDaPagina }: { dados: VisaoGeral; linkDaPagina: (slug: string) => string }) {
  const atual = dados.indicadores.atual;
  // Barra, e não a rosca de seis cores recicladas do painel antigo: com doze
  // ferramentas, a rosca repetia cor e ninguém comparava duas fatias. A ordem é
  // a do banco — a mais avaliada primeiro.
  const itens: ItemDasBarras[] = dados.ferramentas.map((l) => ({
    id: l.slug,
    rotulo: l.nome,
    valor: l.avaliacoes,
    texto: `${numero(l.avaliacoes)} · ${pctLegivel(l.participacao_pct)}`,
    href: linkDaPagina(l.slug),
  }));
  return (
    <Bloco
      titulo="Avaliações por ferramenta"
      atributos={{ "data-avaliacoes-por-ferramenta": true }}
      nota={
        atual.avaliacoes > 0
          ? `Quantas avaliações de alunos cada ferramenta recebeu no período, da mais avaliada para a menos — e a fatia dela nas ${numero(atual.avaliacoes)}.`
          : undefined
      }
    >
      {itens.length === 0 ? (
        <p className="mt-3 text-sm text-cinza">{vazioDoPeriodo(dados.periodo, "Nenhuma ferramenta recebeu avaliação de aluno no período.")}</p>
      ) : (
        <div className="mt-4">
          <BarrasHorizontais
            itens={itens}
            descricao="Avaliações de alunos por ferramenta no período, da mais avaliada para a menos, com a fatia de cada uma no total."
            dado="por-ferramenta"
          />
        </div>
      )}
    </Bloco>
  );
}

// ── 5. A média por ferramenta ───────────────────────────────────────────────

function MediaPorFerramenta({ dados, linkDaPagina }: { dados: VisaoGeral; linkDaPagina: (slug: string) => string }) {
  // Da maior média para a menor, e o nome desempata: duas médias iguais em duas
  // casas não podem trocar de lugar de uma leitura para a outra.
  const itens: ItemDosPontos[] = [...dados.ferramentas]
    .sort(
      (x, y) =>
        (y.media_geral ?? -1) - (x.media_geral ?? -1) || x.nome.localeCompare(y.nome, "pt-BR") || x.slug.localeCompare(y.slug),
    )
    .map((l) => ({
      id: l.slug,
      rotulo: l.nome,
      valor: l.media_geral,
      meta: l.meta,
      texto: notaLegivel(l.media_geral),
      href: linkDaPagina(l.slug),
    }));
  return (
    <Bloco
      titulo="Média por ferramenta"
      atributos={{ "data-media-por-ferramenta": true }}
      // A legenda da meta e o porquê do eixo em zoom são da própria peça dos
      // pontos; aqui fica só o que ela não diz — o recorte e a ordem.
      nota={itens.length > 0 ? "A média geral de cada ferramenta no período, da maior para a menor." : undefined}
    >
      {itens.length === 0 ? (
        <p className="mt-3 text-sm text-cinza">{vazioDoPeriodo(dados.periodo, "Nenhuma ferramenta recebeu avaliação de aluno no período.")}</p>
      ) : (
        <div className="mt-4">
          <PontosPorLinha
            itens={itens}
            descricao="A média geral de cada ferramenta no período, da maior para a menor, com a meta de cada uma."
            dado="media-por-ferramenta"
          />
        </div>
      )}
    </Bloco>
  );
}

// ── 6. As avaliações baixas recentes ────────────────────────────────────────

function BaixasRecentes({
  dados,
  periodoNaBusca,
  linkDaPagina,
  aoAbrir,
}: {
  dados: VisaoGeral;
  periodoNaBusca: string;
  linkDaPagina: (slug: string) => string;
  aoAbrir: (a: AvaliacaoDaLista) => void;
}) {
  const itens = dados.baixas_recentes;
  const baixasDoPeriodo = dados.indicadores.atual.baixas;
  return (
    <Bloco
      titulo="Avaliações baixas recentes"
      atributos={{ "data-baixas-recentes": true, "data-quantas": itens.length }}
      acao={
        // Só quando existe baixa no período: o link para uma lista vazia é ruído.
        baixasDoPeriodo > 0 && (
          <Link
            to={endereco("/admin/avaliacoes/feedbacks", periodoNaBusca, { media: "baixas" })}
            className="text-sm font-semibold text-link underline-offset-2 hover:underline"
            data-ver-todas-as-baixas
          >
            {baixasDoPeriodo === 1 ? "Ver a avaliação baixa do período" : `Ver todas as ${numero(baixasDoPeriodo)} baixas do período`}
          </Link>
        )
      }
      nota={`Até 10, da mais recente para a mais antiga: as avaliações de média ${FAIXAS.baixaAte} ou menos que trazem comentário. Nos Feedbacks estão todas as baixas, com e sem comentário.${
        itens.length > 0 ? " Clique numa para lê-la inteira." : ""
      }`}
    >
      {itens.length === 0 ? (
        <p className="mt-3 text-sm text-cinza" data-baixas-recentes-vazia>
          {dados.periodo.comparacao === "sem_dado_ainda" ? AINDA_SEM_AVALIACAO : "Nenhuma avaliação baixa com comentário no período."}
        </p>
      ) : (
        // Duas colunas a partir de `lg`: dez itens de comentário em duas linhas,
        // um embaixo do outro, passam de mil pixels, e este bloco não é dos que
        // podem ser longos (o diagnóstico e as barras são). A ordem do DOM
        // continua a da mais recente para a mais antiga — é a que o leitor de
        // tela percorre, e a que o olho lê, linha a linha.
        <ul className="mt-3 grid gap-2 lg:grid-cols-2">
          {itens.map((a) => (
            <LinhaDaBaixa key={a.seq} a={a} link={linkDaPagina(a.card)} aoAbrir={aoAbrir} />
          ))}
        </ul>
      )}
    </Bloco>
  );
}

function LinhaDaBaixa({ a, link, aoAbrir }: { a: AvaliacaoDaLista; link: string; aoAbrir: (a: AvaliacaoDaLista) => void }) {
  const q = quandoLegivel(a.em);

  function aoClicarNaLinha() {
    // Quem seleciona o comentário para copiar não quer abrir o detalhe.
    if (window.getSelection()?.toString()) return;
    aoAbrir(a);
  }

  return (
    // O clique no meio da linha abre também, e o foco volta ao BOTÃO dela
    // quando o diálogo fecha: o diálogo acha a linha por `data-avaliacao` e foca
    // o primeiro botão de dentro — que por isso é o do dia e da hora.
    <li
      className="cursor-pointer rounded-xl border border-borda p-3 transition hover:border-acento"
      onClick={aoClicarNaLinha}
      data-avaliacao={a.seq}
      data-media={a.media}
    >
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
        <SeloDaMedia media={a.media} />
        <Link
          to={link}
          onClick={(e) => e.stopPropagation()}
          className="text-sm font-semibold text-link underline-offset-2 hover:underline"
          data-abrir-ferramenta={a.card}
        >
          {a.nome}
        </Link>
        <span className="text-xs text-cinza" data-origem={a.origem}>
          do {ROTULO_DA_ORIGEM[a.origem]}
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            aoAbrir(a);
          }}
          className="rounded text-xs tabular-nums text-cinza underline decoration-dotted underline-offset-2 hover:text-tinta sm:ml-auto"
          data-abrir-avaliacao={a.seq}
        >
          {q.dia} {horaComArtigo(q.hora)}
          <span className="sr-only"> — ler a avaliação inteira</span>
        </button>
      </div>
      <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-tinta" data-comentario>
        {a.comentario ?? ""}
      </p>
    </li>
  );
}

// ── 7. O diagnóstico por ferramenta ─────────────────────────────────────────

/** A menor das três notas: é ela que ordena a tabela e ganha o contorno. */
function piorNota(l: LinhaDaFerramenta): number | null {
  const notas = NOTAS.map((n) => l[n.campo]).filter((v): v is number => v !== null);
  return notas.length ? Math.min(...notas) : null;
}

function Diagnostico({ dados, linkDaPagina }: { dados: VisaoGeral; linkDaPagina: (slug: string) => string }) {
  // A pior nota primeiro: a média geral esconde onde a ferramenta perde — se é
  // pouco útil, difícil de usar ou deixa o aluno insatisfeito —, e é essa a
  // pergunta que o diagnóstico responde. O nome desempata.
  const linhas = [...dados.ferramentas].sort(
    (x, y) =>
      (piorNota(x) ?? Infinity) - (piorNota(y) ?? Infinity) || x.nome.localeCompare(y.nome, "pt-BR") || x.slug.localeCompare(y.slug),
  );
  return (
    <Bloco
      titulo="Diagnóstico por ferramenta"
      atributos={{ "data-diagnostico": true }}
      nota={
        linhas.length > 0
          ? "As três notas de cada ferramenta no período, ordenadas pela menor das três: a ferramenta com a nota mais baixa vem primeiro. A cor é a faixa de cada nota, a mesma da média de uma avaliação."
          : undefined
      }
    >
      {linhas.length === 0 ? (
        <p className="mt-3 text-sm text-cinza">{vazioDoPeriodo(dados.periodo, "Sem avaliação de aluno no período.")}</p>
      ) : (
        <>
          {/* `relative` é o que prende os `sr-only` das células AQUI dentro: eles
              têm posição absoluta, e sem um ancestral posicionado no meio o
              bloco deles é a página inteira — escapavam da rolagem e esticavam
              a página a 375px (463px de largura, medido pela prova de tela). */}
          <div className="relative mt-4 overflow-x-auto">
            <table className="w-full min-w-[34rem] text-sm">
              <caption className="sr-only">
                As três notas de cada ferramenta no período, ordenadas pela pior das três, com o número de avaliações
              </caption>
              <thead>
                <tr className="border-b border-borda text-xs text-cinza">
                  <th scope="col" className="py-2 pr-4 text-left font-semibold">
                    Ferramenta
                  </th>
                  {NOTAS.map((n) => (
                    <th key={n.campo} scope="col" className="px-2 py-2 text-center font-semibold">
                      {n.rotulo}
                    </th>
                  ))}
                  <th scope="col" className="py-2 pl-2 text-right font-semibold">
                    Avaliações
                  </th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((l) => {
                  const pior = piorNota(l);
                  return (
                    <tr key={l.slug} className="border-b border-borda/60 last:border-0" data-diagnostico-ferramenta={l.slug}>
                      <th scope="row" className="py-2 pr-4 text-left font-normal">
                        <Link
                          to={linkDaPagina(l.slug)}
                          className="font-semibold text-link underline-offset-2 hover:underline"
                          data-abrir-ferramenta={l.slug}
                        >
                          {l.nome}
                        </Link>
                      </th>
                      {NOTAS.map((n) => (
                        <CelulaDaNota key={n.campo} campo={n.campo} valor={l[n.campo]} menor={pior !== null && l[n.campo] === pior} />
                      ))}
                      <td className="py-2 pl-2 text-right tabular-nums text-tinta" data-avaliacoes={l.avaliacoes}>
                        {numero(l.avaliacoes)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <ul className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-cinza" data-legenda-do-diagnostico>
            <li>
              <span className={`rounded-md px-2 py-0.5 font-semibold ${TOM_DA_FAIXA.satisfeita}`}>
                {FAIXAS.satisfeitaDesde} ou mais
              </span>
            </li>
            <li>
              <span className={`rounded-md px-2 py-0.5 font-semibold ${TOM_DA_FAIXA.intermediaria}`}>
                acima de {FAIXAS.baixaAte} e abaixo de {FAIXAS.satisfeitaDesde}
              </span>
            </li>
            <li>
              <span className={`rounded-md px-2 py-0.5 font-semibold ${TOM_DA_FAIXA.baixa}`}>
                <span aria-hidden="true">▼ </span>
                {FAIXAS.baixaAte} ou menos — baixa
              </span>
            </li>
            <li className="flex items-center gap-2">
              <span className="rounded-md px-2 py-0.5 font-semibold text-tinta ring-2 ring-current ring-inset" aria-hidden="true">
                contorno
              </span>
              a menor das três notas da ferramenta, que decide a ordem da tabela
            </li>
          </ul>
        </>
      )}
    </Bloco>
  );
}

function CelulaDaNota({ campo, valor, menor }: { campo: CampoDaNota; valor: number | null; menor: boolean }) {
  if (valor === null) {
    return (
      <td className="px-2 py-1.5 text-center text-cinza" data-nota={campo} data-faixa="">
        —
      </td>
    );
  }
  const faixa = faixaDaMedia(valor);
  return (
    <td className="px-2 py-1.5" data-nota={campo} data-faixa={faixa} data-valor={valor} data-menor-da-linha={menor ? "true" : undefined}>
      <span
        className={`mx-auto flex w-20 items-center justify-center gap-1 rounded-md py-1 text-sm font-semibold tabular-nums ${TOM_DA_FAIXA[faixa]} ${
          menor ? "ring-2 ring-current ring-inset" : ""
        }`}
      >
        {faixa === "baixa" && (
          <span aria-hidden="true" className="text-[0.65rem]">
            ▼
          </span>
        )}
        {notaLegivel(valor)}
        {faixa === "baixa" && <span className="sr-only"> (baixa)</span>}
        {menor && <span className="sr-only"> (a menor desta ferramenta)</span>}
      </span>
    </td>
  );
}

// ── 8. Como se lê ───────────────────────────────────────────────────────────

function ComoSeLe() {
  return (
    <Bloco titulo="Como se lê" atributos={{ "data-como-se-le": true }}>
      <p className="mt-2 max-w-3xl text-sm leading-relaxed text-cinza">
        A média de uma avaliação é a média das suas três notas — utilidade, facilidade e satisfação —, e a média geral é
        a média dessas médias. As notas vão de 0 a 10: o formulário do hub vai de 1 a 10, e o app antigo aceitava
        zero. <strong className="font-semibold text-tinta">Satisfeitas</strong> são as
        avaliações de média {FAIXAS.satisfeitaDesde} ou mais; <strong className="font-semibold text-tinta">baixas</strong>,
        as de média {FAIXAS.baixaAte} ou menos. O <strong className="font-semibold text-tinta">NPS adaptado</strong> é o
        percentual de promotores (média {FAIXAS.promotorDesde} ou mais) menos o de detratores (média abaixo de{" "}
        {FAIXAS.detratorAbaixoDe}). A equipe fica fora de todas as contas. Os dias são os de São Paulo, de meia-noite a
        meia-noite, e a variação compara cada número com o mesmo número de dias logo antes, na unidade dele: as contagens
        em %, a média e o NPS adaptado em pontos, os percentuais em pontos percentuais. «Todos» não tem com o que
        comparar.
      </p>
    </Bloco>
  );
}
